import { useCallback } from 'react';
import { useSelectedVersion } from '@client/store/ui';
import { Page } from '@client/store/ui/ui.model';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { buildDeleteImportProjectChanges } from '@shared/helpers/candidate-discard.helpers';

/**
 * Removes an element import from the selected version (undoable). Only the
 * project-side references are dropped — the import's DB rows are left intact so
 * an undo can restore the file. Imports left unreferenced by every version are
 * garbage-collected server-side once old enough (see `listProjectImports`).
 */
export const useDeleteElementImport = (
  importId: string,
  candidateRowIds: readonly string[],
) => {
  const version = useSelectedVersion();
  const applyChanges = useApplyProjectChanges();
  const navigateTo = useProjectNavigation();

  return useCallback(async () => {
    if (!version) return;
    const result = await applyChanges(
      buildDeleteImportProjectChanges(version, importId, [...candidateRowIds]),
    );
    if (result.cancelled) return;
    const remainingImportIds = (version.imports?.importIds ?? []).filter(
      (id) => id !== importId,
    );
    if (remainingImportIds.length === 0) {
      void navigateTo({ page: Page.Elements, elementId: 'none' });
    } else {
      void navigateTo({ elementId: 'none' });
    }
  }, [version, applyChanges, importId, candidateRowIds, navigateTo]);
};
